import type { CreateUpdateOrderDTO, OrderAddressDTO, OrderDTO, OrderShippingDTO } from 'ecosystem'
import { transformOrderItemsToCreateOrderItems } from 'api/utils/orders'
import { carrierFieldsFromSelectedMethod } from 'shared-utils'
import { type UpdateDefaultShipmentRequest } from './types'

interface BuildArgs extends Pick<UpdateDefaultShipmentRequest, 'defaultDetails' | 'shippingCost'> {
  /** The persisted order, as the GET returned it. */
  existing: OrderDTO
  orderNumber: string
  customerId?: string
}

/**
 * Merge the FE's delivery selection into the persisted order.
 *
 * Pure, so the PICKUP/DELIVERY branching can be tested without a round-trip —
 * it is the whole substance of this endpoint, and it was previously inlined in
 * the handler where only an HTTP test could reach it.
 *
 * Writes the v1 flat shipping fields (`shippingAddress`, `shippingCost`,
 * `shippingInfo.*`) because the storefront orders client targets the v1
 * endpoint (`PUT /storefront/orders`), which ignores a `shipments[]` array on
 * input — v1 reconstructs `shipments[0]` from the flat fields on save (see
 * `FEATURE_ORDER_PLACEMENT.md §5.2`).
 */
export const buildDefaultShipmentPayload = ({
  existing,
  orderNumber,
  defaultDetails,
  shippingCost,
  customerId
}: BuildArgs): CreateUpdateOrderDTO => {
  const pickup = defaultDetails.selectedPickup
  const pickupAddress: OrderAddressDTO | undefined =
    defaultDetails.isPickupFromStore && pickup?.address
      ? {
          address1: pickup.address.address1 ?? '',
          address2: pickup.address.address2 ?? '',
          country: pickup.address.country ?? '',
          mobilePhone: pickup.address.mobilePhone ?? '',
          postalCode: pickup.address.postalCode ?? '',
          postalRegion: pickup.address.postalRegion ?? '',
          attention: pickup.address.attention ?? '',
          reference: pickup.address.reference ?? ''
        }
      : undefined

  // Canonical `shippingAddress` for the BE: pickup address takes precedence
  // when PICKUP, otherwise the FE-supplied shipping address (only set when the
  // FE owns the address — `showShippingAddress=true` on the consumer, or
  // invoice flow without a payment widget).
  const effectiveShippingAddress =
    pickupAddress ?? defaultDetails.shippingAddress ?? existing.shippingAddress

  const nextShippingInfo: OrderShippingDTO = {
    ...existing.shippingInfo,
    shippingType: defaultDetails.isPickupFromStore ? 'PICKUP' : 'DELIVERY',
    // Mirror the chosen method onto the canonical carrier fields, the same way
    // the invoice flow does — otherwise an UPFRONT order records the choice
    // only inside `additionalInfo`. These are the fields the read paths key on:
    // `ConfirmationPageStd` shows `carrierName` as the shipping method,
    // `ShipmentCard`'s header is `carrierName — carrierService`, and
    // `hasShipmentData` counts them when deciding whether a synthetic shipment
    // is worth rendering at all (it ignores `shippingType` and `address`).
    ...carrierFieldsFromSelectedMethod(defaultDetails.selectedMethod),
    ...(pickup?.id ? { pickupLocationId: pickup.id } : {}),
    ...(pickup?.name ? { pickupLocationName: pickup.name } : {}),
    ...(pickupAddress ? { pickupAddress } : {}),
    additionalInfo: {
      ...existing.shippingInfo?.additionalInfo,
      defaultDetails
    }
  }

  return {
    ...existing,
    orderNumber: existing.orderNumber ?? orderNumber,
    ...(customerId ? { customerId } : {}),
    items: existing.items ? transformOrderItemsToCreateOrderItems(existing.items) : [],
    ...(shippingCost !== undefined ? { shippingCost } : {}),
    ...(effectiveShippingAddress ? { shippingAddress: effectiveShippingAddress } : {}),
    shippingInfo: nextShippingInfo
  }
}
